'use client';

import { useTranslation } from '@/hooks/useTranslation';
import BookDemoSection from '@/components/BookDemoSection';
import { FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import RealEstateHero from './RealEstateHero';
import IndustryBattleground from '@/components/industry/IndustryBattleground';
import IndustryChallenges from '@/components/industry/IndustryChallenges';
import PlatformCapabilities from '@/components/industry/PlatformCapabilities';
import IndustryResults from '@/components/industry/IndustryResults';
import { getIndustryContent } from '@/lib/industryContent';
import '@/components/industry/industry-page.css';

/* Problem-framing stats for the MetricsBand — sourced from the hero
   (JLL, CBRE, Edelman 2025). */
const METRICS = [
  { value: '73%', label: "of homebuyers say a developer's online reputation directly influences their purchase decision (JLL Global Real Estate Transparency Index 2025)" },
  { value: '60%', label: 'of real estate brand trust crises originate from unresolved social media complaints — not formal regulatory filings (CBRE Global Investor Intentions Survey 2025)' },
  { value: '3 in 5', label: 'luxury homebuyers research developers across social media and property review platforms before visiting a show flat (Edelman Trust Barometer Real Estate 2025)' },
];

export default function RealEstateBody() {
  const { t } = useTranslation();
  const C = getIndustryContent('real-estate');

  const CHALLENGES = [
    {
      title: t("industries_real_estate.the_possession_delay_flashpoint"),
      desc: t("industries_real_estate.a_homebuyer_who_receives_their_keys"),
    },
    {
      title: t("industries_real_estate.the_project_launch_window"),
      desc: t("industries_real_estate.project_launch_sentiment_monitoring"),
    },
    {
      title: t("industries_real_estate.the_regulatory_escalation_risk"),
      desc: t("industries_real_estate.every_unresolved_social_media_compl"),
    },
    {
      title: t("industries_real_estate.the_influencer_broker_blind_spot"),
      desc: t("industries_real_estate.real_estate_influencers_and_broker"),
    },
  ];

  const FAQS = [
    {
      q: t("industries_real_estate.what_is_a_real_estate_developer_bra"),
      a: t("industries_real_estate.one_workspace_for_homebuyer_complai"),
    },
    {
      q: t("industries_real_estate.how_does_locobuzz_help_real_estate"),
      a: t("industries_real_estate.signalsense_detects_buyer_coordinat"),
    },
    {
      q: t("industries_real_estate.can_locobuzz_monitor_project_launch"),
      a: t("industries_real_estate.yes_launch_intelligence_dashboard_t"),
    },
    {
      q: t("industries_real_estate.how_does_locobuzz_monitor_real_esta"),
      a: t("industries_real_estate.paid_and_organic_influencer_content"),
    },
    {
      q: t("industries_real_estate.can_locobuzz_help_real_estate_brand"),
      a: t("industries_real_estate.yes_competitor_developer_mention_vo"),
    },
    {
      q: t("industries_real_estate.does_locobuzz_aggregate_and_manage"),
      a: t("industries_real_estate.yes_developer_and_project_reviews_f"),
    },
  ];

  const BG_TITLE = t('industries_real_estate.why_is_homebuyer_trust_management_n');
  const BG_ACCENT = 'Real Estate Developers?';
  const _bgCut = BG_TITLE.lastIndexOf(BG_ACCENT);
  const bgTitle = _bgCut > 0 ? BG_TITLE.slice(0, _bgCut).trim() : BG_TITLE;
  const bgAccent = _bgCut > 0 ? BG_TITLE.slice(_bgCut) : undefined;

  return (
    <div className="industry-page-wrap">
      <span className="sr-only">
        {t("industries_real_estate.locobuzz_is_an_ainative_brand_reput")}
      </span>
      <span className="sr-only">
        {t("industries_real_estate.homebuyers_research_developers_on_s")}
      </span>
      <span className="sr-only">
        {t("industries_real_estate.locobuzz_maps_six_purposebuilt_capa")}
      </span>
      <span className="sr-only">
        {t("industries_real_estate.real_estate_developers_using_locobu")}
      </span>

      <RealEstateHero />

      <MetricsBand items={METRICS} columns={3} />

      <IndustryBattleground
        eyebrow={t("industries_real_estate.why_homebuyer_trust_is_now_a_salesc")}
        title={bgTitle}
        titleAccent={bgAccent}
        paragraph={t("industries_real_estate.a_developers_social_media_reputatio")}
        imageSrc="/industries/why/real-estate.png"
        imageAlt={t("industries_real_estate.real_estate_developer_brand_reputat_2")}
        bg="tint"
      />

      <IndustryChallenges
        title={t("industries_real_estate.what_brand_cx_challenges_do_real_es")}
        items={CHALLENGES}
        layout="row5"
        align="center"
        bg="page"
      />

      <PlatformCapabilities {...C.capabilities} />

      <IndustryResults {...C.results} />

      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={
            <>
              {t('faq.everything_you_need_to')}{' '}
              <span className="serif-accent italic">{t('faq.know')}</span>
            </>
          }
          align="center"
        />
        <FaqAccordion items={FAQS} columns={2} name="real-estate-faq" />
      </PageSection>

      <BookDemoSection />
    </div>
  );
}
